import {
  MutationCache,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query';
import { captureFrontendException } from '@/observability/sentry';

/**
 * App-wide TanStack Query client. Sane defaults for an authenticated SaaS:
 *  - 5-minute stale time → re-fetches do not happen on every page nav
 *  - 1 retry → tolerate occasional flakes without compounding errors
 *  - no refetch on window focus → avoid surprise calls when alt-tabbing
 *
 * The `QueryCache` + `MutationCache` instances wire a global `onError`
 * sink that forwards every caught query/mutation error to Sentry via
 * `captureFrontendException`. React Query v5 removed the
 * `defaultOptions.queries.onError` field — global error sinks now live
 * on the cache constructors (per
 * https://tanstack.com/query/v5/docs/framework/react/guides/migrating-to-v5#callbacks-on-usequery-have-been-removed).
 * Mutations would still accept `defaultOptions.mutations.onError`, but
 * we use `MutationCache` for symmetry.
 *
 * Per-route or per-hook `onError` callbacks still run as overrides —
 * the cache-level sink fires for every error regardless of whether
 * the call-site declared its own handler, so consumers don't need to
 * remember to call `captureFrontendException` themselves.
 *
 * `captureFrontendException` is a no-op when Sentry hasn't been
 * initialized (dev / test runs without a DSN), so the cache wiring is
 * cost-free in those paths.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err, query) => {
      captureFrontendException(err, {
        source: 'react_query',
        kind: 'query',
        // queryKey is rendered into a short string tag for cardinality
        // control. Sentry tag values must be string|number|boolean.
        query_key: queryKeyTag(query.queryKey),
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      captureFrontendException(err, {
        source: 'react_query',
        kind: 'mutation',
        mutation_key: queryKeyTag(mutation.options.mutationKey),
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Convert a React Query key (array of unknowns) into a low-cardinality
 * string suitable for a Sentry tag. We take only the first segment
 * (the namespace) so per-id keys don't blow up the tag dictionary —
 * `['users', userId]` becomes `users`, not `users:abc-123`.
 */
function queryKeyTag(key: readonly unknown[] | undefined): string {
  if (!key || key.length === 0) return '<unknown>';
  const head = key[0];
  if (typeof head === 'string') return head;
  if (typeof head === 'number' || typeof head === 'boolean') {
    return String(head);
  }
  return '<non-string-head>';
}
